import { isEmpty } from "lodash";
import { type FC, useEffect, useMemo, useState } from "react";

import { type DocumentMetadata } from "@moment/api-collab/api-types";
import { BasicTooltip } from "@moment/design-system/Tooltip";

import { useRouteParams } from "~/data/route";
import { useDocumentActor, useDocumentMetadata } from "~/state";
import { DEFAULT_CANVAS_TITLE } from "~/strings";

import { useCurrentDocument } from "../hooks/useCurrentDocument";
import { EditInPlaceInput } from "./EditInPlaceInput";
import { HeaderPopOver } from "./HeaderPopOver";
import { useNavigationPane } from "./NavigationPaneContext";

export const Header: FC = () => {
	const { documentId } = useRouteParams();

	const document = useDocumentMetadata(documentId);

	if (isEmpty(document)) {
		return null;
	}

	return <HeaderEditor document={document} />;
};

const HeaderEditor: React.FC<{ document: DocumentMetadata }> = ({ document }) => {
	const currentDocument = useCurrentDocument();

	const documentActor = useDocumentActor(document.id);

	const { isReadOnly, isRenamingCanvas, setIsRenamingCanvas, unsetIsRenamingCanvas } =
		useNavigationPane();

	const originalTitle = useMemo(() => {
		if (!document.title) {
			return DEFAULT_CANVAS_TITLE;
		}

		return document.title;
	}, [document]);

	useEffect(() => {
		// Set the title input value to the original title when the original title changes,
		// e.g. when the document is renamed outside of this component
		setTitleInputValue(originalTitle);
	}, [originalTitle]);

	const [titleInputValue, setTitleInputValue] = useState(originalTitle);

	const isRenaming = useMemo(
		() => (isReadOnly ? false : isRenamingCanvas),
		[isReadOnly, isRenamingCanvas]
	);

	const handleOnRenameDocument = () => {
		if (isReadOnly) {
			return;
		}
		setIsRenamingCanvas();
	};

	const saveTitle = () => {
		if (isReadOnly || !currentDocument?.id) {
			return;
		}

		documentActor?.send({
			type: "document.nameChanged",
			payload: {
				newName: titleInputValue,
			},
		});
	};

	const revertTitle = () => {
		setTitleInputValue(originalTitle);
	};

	const handleOnKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
		switch (e.key) {
			case "Enter":
				void saveTitle();
				unsetIsRenamingCanvas();
				break;
			case "Esc":
			case "Escape":
				revertTitle();
				unsetIsRenamingCanvas();
				break;
		}
	};

	const handleOnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const value = e.currentTarget.value;
		// filter out instances of double+ spaces and removes space from start of title string
		// i.e. your title may not have ten spaces in a row between characters
		setTitleInputValue(value);
	};

	const handleOnBlur = () => {
		saveTitle();
	};

	const handleOnFocus = (e: React.FocusEvent<HTMLInputElement>) => {
		e.currentTarget.select();
	};

	if (!currentDocument) {
		return null;
	}

	return (
		<div className="flex min-w-[1rem] items-center truncate px-2">
			<div
				className="flex min-w-0 items-start py-2 font-bold"
				onDoubleClick={handleOnRenameDocument}
			>
				{isRenaming ? (
					<EditInPlaceInput
						textSize="md"
						type="text"
						placeholder={DEFAULT_CANVAS_TITLE}
						value={titleInputValue}
						onChange={handleOnChange}
						onKeyDown={handleOnKeyDown}
						onBlur={handleOnBlur}
						onFocus={handleOnFocus}
						// eslint-disable-next-line jsx-a11y/no-autofocus
						autoFocus
					/>
				) : (
					<BasicTooltip content={titleInputValue}>
						<span className="truncate text-base font-bold">{titleInputValue}</span>
					</BasicTooltip>
				)}
			</div>

			{!isReadOnly && !isRenaming && (
				<HeaderPopOver onRenameCanvas={handleOnRenameDocument} />
			)}
		</div>
	);
};
